/**
 * Pricing Badge Component
 * Color-coded badge for displaying tool pricing tier
 */

import { cn } from "@utils/cn";
import type { PricingTier } from "@/types/tools";

interface PricingBadgeProps {
  tier: PricingTier;
  size?: "sm" | "md";
  className?: string;
}

const pricingConfig: Record<
  PricingTier,
  { label: string; color: string; icon?: string }
> = {
  free: {
    label: "Free",
    color:
      "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800",
  },
  freemium: {
    label: "Freemium",
    color:
      "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800",
  },
  paid: {
    label: "Paid",
    color:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800",
  },
  enterprise: {
    label: "Enterprise",
    color:
      "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-800",
  },
};

export function PricingBadge({
  tier,
  size = "sm",
  className,
}: PricingBadgeProps) {
  const config = pricingConfig[tier];
  if (!config) return null;

  return (
    <span
      className={cn(
        "inline-flex items-center font-medium rounded-full border",
        size === "sm" ? "px-2 py-0.5 text-xs" : "px-2.5 py-1 text-sm",
        config.color,
        className,
      )}
    >
      {config.label}
    </span>
  );
}

/**
 * Pricing tier description for tooltips or detail views
 */
// eslint-disable-next-line react-refresh/only-export-components
export function getPricingDescription(tier: PricingTier): string {
  switch (tier) {
    case "free":
      return "Completely free to use with no limitations";
    case "freemium":
      return "Free tier available with paid upgrades";
    case "paid":
      return "Requires payment to use";
    case "enterprise":
      return "Custom enterprise pricing available";
    default:
      return "";
  }
}
